import type { InfiniteData } from "@tanstack/react-query";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../client";
import type { Creator, CreatorSummary, Page, Post, ProductSummary } from "../types";
import { createOptimisticHandlers } from "./optimistic";
import { qk } from "./qk";
import type { FeedTab } from "./qk";

const FEED_TABS: FeedTab[] = ["for-you", "following", "trending"];

export const suggestedCreatorsQuery = () =>
  queryOptions({
    queryKey: qk.creators.suggested(),
    queryFn: () => apiGet<CreatorSummary[]>("/api/creators?suggested=true"),
    staleTime: 300_000, // plan §5: entity-adjacent list
  });

export const creatorQuery = (username: string) =>
  queryOptions({
    queryKey: qk.creators.detail(username),
    queryFn: () => apiGet<Creator>(`/api/creators/${username}`),
    staleTime: 300_000, // plan §5: entity detail
  });

export const creatorPostsInfiniteQuery = (username: string) =>
  infiniteQueryOptions({
    queryKey: qk.creators.posts(username),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      apiGet<Page<Post>>(`/api/creators/${username}/posts?limit=20${pageParam ? `&cursor=${pageParam}` : ""}`),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: Page<Post>) => lastPage.nextCursor,
    staleTime: 120_000,
  });

export const creatorProductsInfiniteQuery = (username: string) =>
  infiniteQueryOptions({
    queryKey: qk.creators.products(username),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      apiGet<Page<ProductSummary>>(`/api/creators/${username}/products?limit=20${pageParam ? `&cursor=${pageParam}` : ""}`),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: Page<ProductSummary>) => lastPage.nextCursor,
    staleTime: 120_000,
  });

function patchPostAuthors(username: string, isFollowing: boolean) {
  return (previous: InfiniteData<Page<Post>> | undefined): InfiniteData<Page<Post>> | undefined => {
    if (!previous) return previous;
    return {
      ...previous,
      pages: previous.pages.map((page) => ({
        ...page,
        items: page.items.map((post) => (post.author.username === username ? { ...post, author: { ...post.author, isFollowing } } : post)),
      })),
    };
  };
}

function patchSuggestedList(username: string, isFollowing: boolean) {
  return (previous: CreatorSummary[] | undefined): CreatorSummary[] | undefined =>
    previous?.map((creator) => (creator.username === username ? { ...creator, isFollowing } : creator));
}

function patchCreatorDetail(isFollowing: boolean) {
  return (previous: Creator | undefined): Creator | undefined =>
    previous ? { ...previous, isFollowing, followers: previous.followers + (isFollowing ? 1 : -1) } : previous;
}

export interface FollowCreatorVariables {
  username: string;
  following: boolean;
}

/**
 * Follows/unfollows a creator, patching every cached representation this
 * layer knows about: the full profile, the suggested-creators list, and
 * that creator's author summary in every currently-cached feed page (plan
 * §5: "a follow must update every cached representation of that creator").
 */
export function useFollowCreator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ username, following }: FollowCreatorVariables) =>
      following
        ? apiPost<{ isFollowing: boolean; followers: number }>(`/api/creators/${username}/follow`)
        : apiDelete<{ isFollowing: boolean; followers: number }>(`/api/creators/${username}/follow`),
    ...createOptimisticHandlers<FollowCreatorVariables>({
      queryClient,
      action: "follow-creator",
      affectedKeys: ({ username }) => [
        qk.creators.detail(username),
        qk.creators.suggested(),
        ...FEED_TABS.map((tab) => qk.feed(tab)),
      ],
      apply: ({ username, following }) => [
        { queryKey: qk.creators.detail(username), updater: patchCreatorDetail(following) },
        { queryKey: qk.creators.suggested(), updater: patchSuggestedList(username, following) },
        ...FEED_TABS.map((tab) => ({ queryKey: qk.feed(tab), updater: patchPostAuthors(username, following) })),
      ],
    }),
  });
}
